import React from "react";
import { Box } from "@mui/material";

import Panel from "../../../ProductAnalyzer/partials/QuickInfoCard/Panel";
import { Tooltip } from "../../../../components";

import {
  formatCurrency,
  formatPercent,
  formatNumber,
} from "../../../../helpers";
import { ILookup } from "../../../../features/lookups/lookups.types";
import { useAppSelector } from "../../../../store/hooks";
import { selectProductAnalyzerSettings } from "../../../../features/settings/settings.selector";
import { getBsrDisplay } from "../../../ProductAnalyzer/partials/QuickInfoCard";

interface InfoPanelGroupProps {
  item: ILookup;
}

const InfoPanelGroup: React.FC<InfoPanelGroupProps> = ({ item }) => {
  const { minBsr, maxBsr } = useAppSelector(selectProductAnalyzerSettings);
  const bsrPercent: number = Math.ceil(
    (item.bsr / item.categoryProductCount) * 100,
  );

  return (
    <Box
      display={"grid"}
      gap={"4px"}
      flexWrap={"wrap"}
      flexDirection={"row"}
      gridTemplateColumns={{
        lg: "1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr",
        sm: "1fr 1fr 1fr 1fr",
        xs: "1fr 1fr",
      }}
    >
      <Panel title="Cost Price" variant="info">
        {formatCurrency(item.costPrice)}
      </Panel>
      <Panel title="Sale Price" variant="info">
        {formatCurrency(item.salePrice)}
      </Panel>
      <Panel
        title="Profit"
        variant={item.profit >= item.minProfit ? "success" : "fail"}
      >
        <Tooltip
          title={
            item.profit >= item.minProfit ? (
              <>&#x1F44D; This meets your minimum profit criteria.</>
            ) : (
              <>&#x1F44E; This does not meet your minimum profit criteria.</>
            )
          }
        >
          <Box>{formatCurrency(item.profit)}</Box>
        </Tooltip>
      </Panel>
      <Panel
        title="Profit %"
        variant={item.profit >= item.minProfit ? "success" : "fail"}
      >
        <Tooltip
          title={
            item.profit >= item.minProfit ? (
              <>&#x1F44D; This meets your minimum profit criteria.</>
            ) : (
              <>&#x1F44E; This does not meet your minimum profit criteria.</>
            )
          }
          arrow
        >
          <Box>{formatPercent(item.profitMargin)}</Box>
        </Tooltip>
      </Panel>
      <Panel title="ROI" variant={item.roi >= item.minRoi ? "success" : "fail"}>
        <Tooltip
          title={
            item.roi >= item.minRoi ? (
              <>&#x1F44D; This meets your minimum ROI criteria.</>
            ) : (
              <>&#x1F44E; This does not meet your minimum ROI criteria.</>
            )
          }
        >
          <Box>{formatPercent(item.roi)}</Box>
        </Tooltip>
      </Panel>
      <Panel
        title="BSR"
        variant={
          bsrPercent >= minBsr && bsrPercent <= maxBsr ? "success" : "fail"
        }
      >
        {item.bsr > 0 ? (
          <Box>{getBsrDisplay(item.bsr, item.categoryProductCount)}</Box>
        ) : (
          <Tooltip
            title={<>&#x26A0;&#xFE0F; We do not know BSR of this product.</>}
            arrow
          >
            <Box>Unknown</Box>
          </Tooltip>
        )}
      </Panel>
      <Panel
        title="EST. Sales"
        variant={item.estimatedSales > 0 ? "info" : "fail"}
      >
        <Tooltip
          title={
            item.estimatedSales > 0 ? (
              <>
                &#x2139;&#xFE0F; Estimated monthly sales{" "}
                {formatNumber(item.estimatedSales, { forcePlusSuffix: true })}.
              </>
            ) : (
              <>
                &#x26A0;&#xFE0F; We do not have enough data points to estimate
                the sales of this product.
              </>
            )
          }
        >
          <Box>
            {item.estimatedSales > 0
              ? `${formatNumber(item.estimatedSales, { forcePlusSuffix: true })}/mo`
              : "Unknown"}
          </Box>
        </Tooltip>
      </Panel>
      <Panel
        title="Breakeven"
        variant={item.salePrice >= item.breakeven ? "success" : "fail"}
      >
        <Tooltip
          title={
            item.salePrice >= item.breakeven ? (
              <>&#x1F44D; Your sale price is equal to or above breakeven.</>
            ) : (
              <>&#x1F44E; Your sale price is below breakeven.</>
            )
          }
        >
          <Box>{formatCurrency(item.breakeven)}</Box>
        </Tooltip>
      </Panel>
    </Box>
  );
};

export default InfoPanelGroup;
